/**
 * @ringg/react-native — the native widget on the shared @ringg/core brain.
 *
 * Layers, in the order an integrator meets them:
 *   RinggWidget      the assembled UI, mounted over the host app
 *   transport        the LiveKit adapter core drives through its port
 *   platform         microphone permission and the notification tune
 *   lib              theming, gradients, host actions, node ids
 *
 * `@ringg/core` is a devDependency on purpose: it is private and must be
 * bundled into the published output, never resolved from the registry. Its
 * public surface is re-exported here so consumers import from one package.
 */

export { createRinggWidgetController, createCallbackEventBus, type RinggWidgetController, type RinggWidgetConfig, type ControllerPorts, type TransportAdapter } from "@ringg/core";
export * from "@ringg/core/react";

export { RinggWidget, type RinggWidgetProps } from "./RinggWidget";

export { createLiveKitTransport, type LiveKitTransport, type LiveKitTransportOptions } from "./transport/livekit-transport";
export { createAudioSession, type AudioSessionPort } from "./platform/audio-session";
export { createNativeMicPermission } from "./platform/mic-permission";
// The backend allow-lists callers by `Origin`; native apps must present their
// own. Pass the result as `RinggWidgetConfig.clientOrigin`.
export { appOrigin } from "./platform/app-origin";
export { createNotificationPlayer, createSilentNotificationPlayer, type PlayTune } from "./platform/notification";

// The RN counterpart of web's `executeDomAction`: agents trigger host actions,
// and the app decides what they mean.
export { createHostActionDispatcher, type HostAction, type HostActionHandler } from "./lib/host-actions";

// Theming, for hosts that render their own surfaces alongside the widget and
// want them to match.
export { WidgetThemeProvider, useWidgetTheme, resolveButtonRadius, resolveFontFamily, resolveRadius } from "./lib/widget-theme";
export { GradientFill, solidColor } from "./lib/gradient";
export { toSize, toViewStyle } from "./lib/portable-styles";
export { Markdown } from "./lib/markdown";

// `testID={ringgId("header-title")}` — the same node names the web widget
// exposes as `data-ringg`, for end-to-end tests driving both platforms.
export { ringgId } from "./lib/testing";
